/* eslint-disable no-unreachable */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Alert, AlertTitle } from "@material-ui/lab";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import { useUserCreation } from "./hooks/useUserCreation";
import PersonalInfoStep from "./components/shared/forms/PersonalInfoForm";
import AddressInfoStep from "./components/shared/forms/AddressInfoForm";
import DocumentsStep from "./components/shared/forms/DocumentsForm";
import BankingInfoStep from "./components/shared/forms/BankingInfoForm";
import SupplierStep from "./components/shared/forms/SupplierForm";
import ReviewStep from "./components/creation/ReviewStep";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    marginBottom: theme.spacing(4),
  },
  stepper: {
    backgroundColor: "transparent",
  },
  stepContent: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  successMessage: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  stepDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  formWrapper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  reviewWrapper: {
    marginTop: theme.spacing(3),
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
  loadingWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
}));

// Step descriptions to guide users
const stepDescriptions = [
  "Enter the user's personal details and contact information.",
  "Provide the user's address information.",
  "Upload required identification documents.",
  "Enter payment information for the user.",
  "Select product types and suppliers for the user (optional).",
];

/**
 * UserCreation component with proper step handling
 */
export default function UserCreation() {
  const classes = useStyles();
  const {
    formData,
    loading,
    error,
    createdUser,
    updateFormData,
    resetForm,
    submitForm,
    validateStep,
  } = useUserCreation();

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepErrors, setStepErrors] = useState({});
  const [showReviewStep, setShowReviewStep] = useState(false);
  const history = useHistory();

  // Define steps for the user creation process
  const steps = [
    {
      id: 0,
      label: "Personal Information",
      component: PersonalInfoStep,
      required: true,
    },
    {
      id: 1,
      label: "Address Information",
      component: AddressInfoStep,
      required: true,
    },
    {
      id: 2,
      label: "Documents",
      component: DocumentsStep,
      required: true,
    },
    {
      id: 3,
      label: "Banking Information",
      component: BankingInfoStep,
      required: true,
    },
    {
      id: 4,
      label: "Suppliers & Products",
      component: SupplierStep,
      required: false,
    },
  ];

  // Function to handle moving to the next step
  const handleNext = async (stepData = {}) => {
    // Clear previous error for this step
    setStepErrors(prev => ({ ...prev, [activeStep]: null }));

    try {
      // Update form data with step data
      updateFormData(stepData);

      // Get merged data for validation
      const mergedData = { ...formData, ...stepData };

      // Basic validation using the merged data
      const validationError = await validateStep(mergedData, activeStep);

      if (validationError) {
        setStepErrors(prev => ({ ...prev, [activeStep]: validationError }));
        return;
      }

      // Mark step as completed
      if (!completedSteps.includes(activeStep)) {
        setCompletedSteps(prev => [...prev, activeStep]);
      }

      // Move to next step or show review if at last step
      if (activeStep === steps.length - 1) {
        setShowReviewStep(true);
      } else {
        setActiveStep(prevStep => prevStep + 1);
      }
    } catch (err) {
      setStepErrors(prev => ({ ...prev, [activeStep]: err.message }));
    }
  };

  // Function to handle going back to the previous step
  const handleBack = () => {
    if (showReviewStep) {
      setShowReviewStep(false);
    } else if (activeStep > 0) {
      setActiveStep(prevStep => prevStep - 1);
    }
  };

  // Function to skip optional steps
  const handleSkipStep = () => {
    if (!steps[activeStep].required) {
      if (!completedSteps.includes(activeStep)) {
        setCompletedSteps(prev => [...prev, activeStep]);
      }
      if (activeStep === steps.length - 1) {
        setShowReviewStep(true);
      } else {
        setActiveStep(prevStep => prevStep + 1);
      }
    }
  };

  // Function to restart the form
  const handleRestart = () => {
    resetForm();
    setActiveStep(0);
    setCompletedSteps([]);
    setStepErrors({});
    setShowReviewStep(false);
  };

  // Success message with generated password display
  const renderSuccessMessage = () => (
    <Alert
      severity="success"
      variant="filled"
      className={classes.successMessage}
    >
      <AlertTitle>User Created Successfully!</AlertTitle>
      <Typography paragraph>
        The new user has been created and is awaiting approval.
      </Typography>
      <Typography paragraph>
        <strong>User ID:</strong> Z-{createdUser?.id}
      </Typography>
      {createdUser?.generatedPassword && (
        <Box mt={2} p={2} bgcolor="success.dark" borderRadius={1}>
          <Typography variant="body2" style={{ color: "white" }}>
            <strong>Temporary Password:</strong> {createdUser.generatedPassword}
          </Typography>
          <Typography
            variant="caption"
            style={{ color: "rgba(255,255,255,0.8)" }}
          >
            Please share this password securely with the user. They should
            change it after first login.
          </Typography>
        </Box>
      )}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Grid container spacing={2}>
          <Grid item>
            <Button variant="contained" color="primary" onClick={handleRestart}>
              Create Another User
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/manage-dealers")}
            >
              Go to Manage Dealers page
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Alert>
  );

  // Render form component based on the current step
  const renderStepContent = stepIndex => {
    const currentStep = steps[stepIndex];
    const StepComponent = currentStep.component;
    const stepError = stepErrors[stepIndex];

    return (
      <Box className={classes.formWrapper}>
        {stepError && (
          <Alert
            severity="error"
            className={classes.errorAlert}
            icon={<ErrorIcon />}
          >
            {stepError}
          </Alert>
        )}

        <StepComponent
          formData={formData}
          onSave={updateFormData}
          onBack={stepIndex > 0 ? handleBack : null}
          onNext={handleNext}
          submitLabel="Save & Continue"
          showBackButton={stepIndex > 0}
        />

        {/* Optional step skip button */}
        {!currentStep.required && (
          <Box mt={2} display="flex" justifyContent="center">
            <Button variant="outlined" color="default" onClick={handleSkipStep}>
              Skip This Step
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Render the review step
  const renderReviewStep = () => {
    return (
      <Paper className={classes.reviewWrapper} elevation={2}>
        <Box p={3}>
          <Typography variant="h5" gutterBottom>
            Review and Submit
          </Typography>

          <Typography className={classes.stepDescription} paragraph>
            Please review all information below before submitting the user
            registration. Once submitted, the user will be created and await
            approval.
          </Typography>

          {error && (
            <Alert
              severity="error"
              className={classes.errorAlert}
              icon={<ErrorIcon />}
            >
              <Typography variant="body2">
                <strong>Submission Error:</strong> {error}
              </Typography>
            </Alert>
          )}

          <ReviewStep
            formData={formData}
            onBack={handleBack}
            onSubmit={submitForm}
            loading={loading}
            updateFormData={updateFormData}
            submitLabel={
              loading ? (
                <span className={classes.loadingWrapper}>
                  <CircularProgress size={20} />
                  Creating User...
                </span>
              ) : (
                "Create User Account"
              )
            }
          />
        </Box>
      </Paper>
    );
  };

  // Check if we can proceed to review
  const canShowReview = () => {
    const requiredSteps = steps
      .filter(step => step.required)
      .map(step => step.id);
    return requiredSteps.every(stepId => completedSteps.includes(stepId));
  };

  return (
    <div className={classes.root}>
      <Typography component="h1" variant="h4" align="center" gutterBottom>
        Create New User
      </Typography>

      <Typography
        variant="subtitle1"
        align="center"
        color="textSecondary"
        paragraph
      >
        Follow the steps below to create a new user account. All required
        information must be completed.
      </Typography>

      {createdUser ? (
        <Card elevation={3}>
          <CardContent>{renderSuccessMessage()}</CardContent>
        </Card>
      ) : (
        <>
          {showReviewStep ? (
            renderReviewStep()
          ) : (
            <Box>
              {/* Progress indicator */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Progress: Step {activeStep + 1} of {steps.length}
                </Typography>
              </Box>

              {/* Stepper */}
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                className={classes.stepper}
                nonLinear
              >
                {steps.map((step, index) => (
                  <Step
                    key={step.id}
                    completed={completedSteps.includes(step.id)}
                    disabled={index > activeStep + 1}
                  >
                    <StepLabel
                      error={!!stepErrors[step.id]}
                      optional={
                        !step.required ? (
                          <Typography variant="caption">Optional</Typography>
                        ) : null
                      }
                      onClick={() => {
                        // Allow clicking on completed steps or the current/next step
                        if (
                          completedSteps.includes(index) ||
                          index <= activeStep
                        ) {
                          setActiveStep(index);
                          setShowReviewStep(false);
                        }
                      }}
                      style={{
                        cursor:
                          completedSteps.includes(index) || index <= activeStep
                            ? "pointer"
                            : "default",
                      }}
                    >
                      <Typography>{step.label}</Typography>
                    </StepLabel>

                    <StepContent className={classes.stepContent}>
                      <Typography
                        className={classes.stepDescription}
                        color="textSecondary"
                      >
                        {stepDescriptions[index]}
                      </Typography>

                      {index === activeStep && renderStepContent(index)}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {/* Review button - shown when all required steps are complete */}
              {canShowReview() && !showReviewStep && (
                <Box mt={4} display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => setShowReviewStep(true)}
                    startIcon={<CheckCircleIcon />}
                  >
                    Proceed to Review & Submit
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </div>
  );
}
